import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { Filter, X, ChevronRight } from 'lucide-react';

const DESIGNATIONS = [20, 30, 40, 50, 60, 70, 80];

const OCCUPANCY_OPTIONS = [
  { value: 'occupied', label: 'Occupied' },
  { value: 'leased', label: 'Leased' },
  { value: 'notice', label: 'Notice' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'model', label: 'Model' },
  { value: 'offline', label: 'Offline' },
];

export default function AitPropertyUnitFilter({ buildings, propertyId }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [filterBedroom, setFilterBedroom] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterOccupancy, setFilterOccupancy] = useState('all');
  const [expanded, setExpanded] = useState(false);

  // Flatten all units with building info
  const allUnits = useMemo(() => {
    if (!buildings) return [];
    return buildings.flatMap(b =>
      (b.units || []).map(u => ({ ...u, building_id: b.id, building_name: b.name || b.bin_number }))
    );
  }, [buildings]);

  // Available bedrooms from data
  const bedroomOptions = useMemo(() => {
    const brs = [...new Set(allUnits.map(u => u.bedroom_count).filter(b => b != null))].sort((a, b) => a - b);
    return brs;
  }, [allUnits]);

  // Apply filters
  const filteredUnits = useMemo(() => {
    let result = allUnits;
    if (filterBedroom !== 'all') {
      result = result.filter(u => String(u.bedroom_count) === filterBedroom);
    }
    if (filterDesignation !== 'all') {
      if (filterDesignation === 'none') {
        result = result.filter(u => !u.current_designation);
      } else if (filterDesignation === 'market') {
        result = result.filter(u => !u.qualified);
      } else {
        result = result.filter(u => String(u.current_designation) === filterDesignation);
      }
    }
    if (filterOccupancy !== 'all') {
      result = result.filter(u => u.occupancy_status === filterOccupancy);
    }
    return result;
  }, [allUnits, filterBedroom, filterDesignation, filterOccupancy]);

  // Group filtered units by building
  const buildingGroups = useMemo(() => {
    const groups = {};
    filteredUnits.forEach(u => {
      if (!groups[u.building_id]) {
        const b = buildings.find(b => b.id === u.building_id);
        groups[u.building_id] = { building: b, units: [] };
      }
      groups[u.building_id].units.push(u);
    });
    return Object.values(groups).sort((a, b) =>
      (a.building.name || a.building.bin_number || '').localeCompare(b.building.name || b.building.bin_number || '')
    );
  }, [filteredUnits, buildings]);

  const hasActiveFilters = filterBedroom !== 'all' || filterDesignation !== 'all' || filterOccupancy !== 'all';
  const selectCls = `text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`;

  if (allUnits.length === 0) return null;

  return (
    <div className="mb-6">
      <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        {/* Filter bar */}
        <div
          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
          onClick={() => setExpanded(!expanded)}
        >
          <Filter size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            Unit Filter
          </span>
          {hasActiveFilters && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">
              {filteredUnits.length} of {allUnits.length} units
            </span>
          )}
          <div className="flex-1" />
          <ChevronRight size={14} className={`transition-transform ${isDark ? 'text-gray-500' : 'text-gray-400'} ${expanded ? 'rotate-90' : ''}`} />
        </div>

        {expanded && (
          <>
            {/* Filter controls */}
            <div className={`flex items-center gap-3 px-4 py-2 border-t ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-100 bg-gray-50/50'}`}>
              <select value={filterBedroom} onChange={e => setFilterBedroom(e.target.value)} className={selectCls}>
                <option value="all">All Bedrooms</option>
                {bedroomOptions.map(br => (
                  <option key={br} value={String(br)}>{br === 0 ? 'Studio' : `${br} BR`}</option>
                ))}
              </select>
              <select value={filterDesignation} onChange={e => setFilterDesignation(e.target.value)} className={selectCls}>
                <option value="all">All Designations</option>
                {DESIGNATIONS.map(d => (
                  <option key={d} value={String(d)}>{d}% AMI</option>
                ))}
                <option value="none">Undesignated</option>
                <option value="market">Market Rate</option>
              </select>
              <select value={filterOccupancy} onChange={e => setFilterOccupancy(e.target.value)} className={selectCls}>
                <option value="all">All Occupancy</option>
                {OCCUPANCY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={(e) => { e.stopPropagation(); setFilterBedroom('all'); setFilterDesignation('all'); setFilterOccupancy('all'); }}
                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <X size={10} /> Clear
                </button>
              )}
            </div>

            {/* Results */}
            {hasActiveFilters && (
              <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {filteredUnits.length === 0 ? (
                  <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No units match the current filters.
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto">
                    {buildingGroups.map(({ building: b, units: bUnits }) => (
                      <div key={b.id}>
                        {/* Building header */}
                        <div
                          className={`flex items-center justify-between px-4 py-2 sticky top-0 cursor-pointer ${isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}`}
                          onClick={() => navigate(`/dashboard/ait/${propertyId}/buildings/${b.id}`)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {b.name || b.bin_number}
                            </span>
                            {b.bin_number && b.name && (
                              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{b.bin_number}</span>
                            )}
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-500 font-medium">
                              {bUnits.length} unit{bUnits.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <ChevronRight size={12} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
                        </div>

                        {/* Units table */}
                        <table className={`w-full text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <tbody className={`divide-y ${isDark ? 'divide-gray-700/50' : 'divide-gray-100'}`}>
                            {bUnits.map(u => {
                              const occColor =
                                u.occupancy_status === 'vacant' ? 'text-red-500' :
                                u.occupancy_status === 'leased' ? 'text-blue-500' :
                                u.occupancy_status === 'notice' ? 'text-orange-500' :
                                u.occupancy_status === 'offline' || u.occupancy_status === 'model' ? 'text-orange-500' :
                                'text-green-500';
                              return (
                                <tr key={u.id} className={isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50/50'}>
                                  <td className="pl-6 pr-2 py-1.5 font-medium w-20">{u.unit_number}</td>
                                  <td className="px-2 py-1.5 w-14">{u.bedroom_count != null ? (u.bedroom_count === 0 ? 'Studio' : `${u.bedroom_count} BR`) : '—'}</td>
                                  <td className="px-2 py-1.5 w-20">
                                    {u.current_designation ? (
                                      <span className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'} font-medium`}>{u.current_designation}%</span>
                                    ) : u.qualified ? (
                                      <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Undesig.</span>
                                    ) : (
                                      <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Market</span>
                                    )}
                                  </td>
                                  <td className={`px-2 py-1.5 capitalize w-20 ${occColor}`}>{u.occupancy_status || '—'}</td>
                                  <td className={`px-2 py-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{u.tenant_name || ''}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
